import {
  NumericTextBox,
  type NumericTextBoxChangeEvent,
} from "@progress/kendo-react-inputs";
import { useEffect, useId, useRef, useState } from "react";
import { type Country } from "@/app/services/context/CountryContext";
import {
  fetchCountries,
  filterCountriesByCurrency,
} from "@/app/services/localservices/countryService";
import { COUNTRY_BASE_PATH } from "../../../../utils/config";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import type { RootState } from "@/app/redux/store";

interface Props {
  label?: string;
  value: string;
  onChange?: (value: NumericTextBoxChangeEvent) => void;
  onChangeCurrency?: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  validationMessage?: string;
  readonly?: boolean;
  maxAmount?: number;
  allowedCurrencyCodes?: string[];
  small?: boolean;
  userCountryCode?: string;
  initialCurrencyCode?: string;
}

const AmountTextBox = ({
  label,
  value,
  onChange,
  disabled,
  required,
  validationMessage,
  readonly = false,
  maxAmount,
  allowedCurrencyCodes = [],
  onChangeCurrency,
  small = true,
  userCountryCode,
  initialCurrencyCode,
}: Props) => {
  const userData = useSelector((state: RootState) => state.userData.data);

  const [enterValue, setEnterValue] = useState<number | null>(
    value ? parseFloat(value) : null,
  );
  const [inputLength, setInputLength] = useState<number>(0);
  const { t } = useTranslation();
  const [selectedCountry, setSelectedCountry] = useState<Country>();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [searchItems, setSearchItems] = useState<Country[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [allCountries, setAllCountries] = useState<Country[]>([]);
  const [activeOptionId, setActiveOptionId] = useState<string | null>(null);

  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const listboxRef = useRef<HTMLDivElement | null>(null);

  const uid = useId();
  const inputId = `gx-amount-input-${uid}`;
  const errorId = `gx-amount-error-${uid}`;
  const maxLengthErrorId = `gx-amount-maxlen-error-${uid}`;
  const comboboxLabelId = `gx-currency-label-${uid}`;
  const listboxId = `gx-currency-listbox-${uid}`;
  const searchInputId = `gx-currency-search-${uid}`;

  const isValid = validationMessage === undefined;
  const isOverMax = Boolean(maxAmount && maxAmount < inputLength);

  const describedByIds =
    [!isValid ? errorId : null, isOverMax ? maxLengthErrorId : null]
      .filter(Boolean)
      .join(" ") || undefined;

  // ---------------------------------------------

  useEffect(() => {
    setEnterValue(value ? parseFloat(value) : null);
  }, [value]);

  const handleOnChange = (event: NumericTextBoxChangeEvent) => {
    setEnterValue(event?.target.value);
    if (onChange) {
      onChange(event);
    }
  };

  useEffect(() => {
    if (enterValue) {
      const input = parseInt(enterValue?.toString());
      setInputLength(input?.toString()?.length);
    }
  }, [enterValue]);

  const closeDropdown = (returnFocus: boolean) => {
    setDropdownOpen(false);
    setSearchValue("");
    setSearchItems(countries);
    setActiveOptionId(null);
    if (returnFocus) {
      triggerRef.current?.focus();
    }
  };

  const toggleDropdown = () => {
    setDropdownOpen((prev) => !prev);
  };

  const handleCountrySelect = (item: Country) => {
    setSelectedCountry(item);
    closeDropdown(true);
    if (onChangeCurrency) {
      onChangeCurrency(item.currency.code);
    }
  };

  const searchCountry = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);

    if (!value.trim()) {
      setSearchItems(countries);
      return;
    }

    const lowerValue = value.toLowerCase();
    const filtered = countries.filter(
      (item) =>
        item.name.toLowerCase().includes(lowerValue) ||
        item.currency.code.toLowerCase().includes(lowerValue) ||
        item.currency.name?.toLowerCase().includes(lowerValue),
    );

    setSearchItems(filtered);
  };

  // Fetch all countries on mount
  useEffect(() => {
    const getCountries = async () => {
      try {
        const result = await fetchCountries();
        setAllCountries(result);

        let defaultCountry: Country | undefined;

        // Highest priority: a previously-saved currency (e.g. reopening
        // an existing threshold that already has a currency chosen).
        if (initialCurrencyCode && result) {
          defaultCountry = result.find(
            (country) =>
              country.currency.code?.toUpperCase() ===
              initialCurrencyCode.toUpperCase(),
          );
        }

        if (!defaultCountry && userCountryCode && result) {
          defaultCountry = result.find(
            (country) =>
              country.code?.toUpperCase() === userCountryCode.toUpperCase(),
          );
        }

        if (!defaultCountry && userData?.currencyCode && result) {
          defaultCountry = result.find((country) => {
            return (
              country.currency.code?.toUpperCase() ===
              userData?.currencyCode.toUpperCase()
            );
          });
        }

        if (!defaultCountry && allowedCurrencyCodes.length > 0 && result) {
          const filteredCountries = filterCountriesByCurrency(
            result,
            allowedCurrencyCodes,
          );
          defaultCountry = filteredCountries[124];
        }

        if (!defaultCountry && result.length > 0) {
          defaultCountry = result[124];
        }

        if (defaultCountry) {
          setSelectedCountry(defaultCountry);
          if (onChangeCurrency) {
            onChangeCurrency(defaultCountry.currency.code);
          }
        }
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };

    getCountries();
  }, [userCountryCode, initialCurrencyCode]);

  useEffect(() => {
    const initializeCountries = async () => {
      if (allowedCurrencyCodes.length > 0 && allCountries.length > 0) {
        const filteredCountries = filterCountriesByCurrency(
          allCountries,
          allowedCurrencyCodes,
        );

        const isSelectedInFiltered =
          selectedCountry &&
          filteredCountries.some(
            (country) => country.id === selectedCountry.id,
          );

        let defaultCountry = filteredCountries[124];

        if (!isSelectedInFiltered) {
          if (initialCurrencyCode) {
            const savedCountry = filteredCountries.find(
              (country) =>
                country.currency.code?.toUpperCase() ===
                initialCurrencyCode.toUpperCase(),
            );
            if (savedCountry) {
              defaultCountry = savedCountry;
            }
          } else if (userCountryCode) {
            const userCountry = filteredCountries.find(
              (country) =>
                country.code?.toUpperCase() === userCountryCode.toUpperCase(),
            );
            if (userCountry) {
              defaultCountry = userCountry;
            }
          }

          setSelectedCountry(defaultCountry);
          if (onChangeCurrency) {
            onChangeCurrency(defaultCountry.currency.code);
          }
        }

        setCountries(filteredCountries);
        setSearchItems(filteredCountries);
      }
    };

    initializeCountries();
  }, [
    allowedCurrencyCodes,
    allCountries,
    userCountryCode,
    initialCurrencyCode,
  ]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
        setSearchValue("");
        setSearchItems(countries);
        setActiveOptionId(null);
      }
    };

    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen, countries]);

  useEffect(() => {
    if (dropdownOpen) {
      setSearchValue("");
      setSearchItems(countries);
      searchInputRef.current?.focus();
    }
  }, [dropdownOpen]);

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      closeDropdown(true);
      return;
    }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (searchItems.length === 0) return;
      const currentIndex = searchItems.findIndex(
        (item) => `gx-currency-option-${item.id}` === activeOptionId,
      );
      let nextIndex: number;
      if (e.key === "ArrowDown") {
        nextIndex =
          currentIndex + 1 >= searchItems.length ? 0 : currentIndex + 1;
      } else {
        nextIndex =
          currentIndex - 1 < 0 ? searchItems.length - 1 : currentIndex - 1;
      }
      const nextId = `gx-currency-option-${searchItems[nextIndex].id}`;
      setActiveOptionId(nextId);
      document.getElementById(nextId)?.scrollIntoView({ block: "nearest" });
    } else if (e.key === "Enter") {
      e.preventDefault();
      const active = searchItems.find(
        (item) => `gx-currency-option-${item.id}` === activeOptionId,
      );
      if (active) {
        handleCountrySelect(active);
      }
    }
  };

  return (
    <>
      <div>
        {label && (
          <label htmlFor={inputId} className="gx-font-bold">
            {label}
            {required && <span aria-hidden="true"> *</span>}
          </label>
        )}
        <div
          className={`gx-amount-input-container ${
            isValid ? "gx-dropdown-container" : "gx-dropdown-invalid"
          }`}
        >
          <div className={small === true ? "w-[73%]" : "w-[83%]"}>
            <NumericTextBox
              id={inputId}
              value={enterValue}
              onChange={handleOnChange}
              spinners={false}
              disabled={disabled}
              className="gx-textbox-css amount-input gx-font-extra-bold gx-textbox-css custom-placeholder"
              readOnly={readonly}
              max={maxAmount}
              placeholder="0.00"
              min={0}
              format="n2"
              required={required}
              aria-invalid={!isValid || isOverMax}
              aria-describedby={describedByIds}
              aria-required={required}
            />
          </div>
          <div
            className={
              small ? "input-suffix-wrapper-small" : "input-suffix-wrapper"
            }
          >
            <div className="suffix-divider"></div>
            <div className="gx-input-suffix-item">
              <div ref={dropdownRef}>
                <span id={comboboxLabelId} className="sr-only">
                  {t("currency")}
                </span>
                <button
                  ref={triggerRef}
                  type="button"
                  className={` ${
                    allowedCurrencyCodes.length > 0
                      ? "gx-cs-trigger-1"
                      : "gx-cs-trigger-2"
                  }`}
                  onClick={toggleDropdown}
                  disabled={disabled}
                  aria-haspopup="listbox"
                  aria-expanded={dropdownOpen}
                  aria-controls={listboxId}
                  aria-labelledby={
                    selectedCountry
                      ? `${comboboxLabelId} gx-currency-value-${uid}`
                      : comboboxLabelId
                  }
                >
                  {selectedCountry && (
                    <>
                      <img
                        className="gx-cs-flag-1"
                        src={`${COUNTRY_BASE_PATH}${selectedCountry.flagIcon}`}
                        alt=""
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                      <span
                        id={`gx-currency-value-${uid}`}
                        className="gx-font-bold"
                      >
                        {selectedCountry.currency.code}
                      </span>
                    </>
                  )}
                </button>
                {dropdownOpen && allowedCurrencyCodes.length > 0 && (
                  <div className="gx-cs-dropdown-1">
                    <div className="gx-cs-search-wrapper">
                      <label htmlFor={searchInputId} className="sr-only">
                        {t("search_country_or_currency")}
                      </label>
                      <input
                        ref={searchInputRef}
                        id={searchInputId}
                        onChange={searchCountry}
                        onKeyDown={handleSearchKeyDown}
                        value={searchValue}
                        type="text"
                        role="combobox"
                        aria-expanded={dropdownOpen}
                        aria-controls={listboxId}
                        aria-activedescendant={activeOptionId ?? undefined}
                        aria-autocomplete="list"
                        className="gx-cs-search-input"
                        placeholder={t(
                          "search_country_or_currency_placeholder",
                        )}
                        autoFocus
                      />
                    </div>

                    <div
                      ref={listboxRef}
                      id={listboxId}
                      role="listbox"
                      aria-labelledby={comboboxLabelId}
                      className="gx-cs-list-container"
                      style={{ maxHeight: "200px", overflowY: "auto" }}
                    >
                      {searchItems.length === 0 && (
                        <div className="gx-cs-item" role="status">
                          {t("no_results_found")}
                        </div>
                      )}
                      {searchItems?.map((item) => {
                        const optionId = `gx-currency-option-${item.id}`;
                        const isSelected = selectedCountry?.id === item.id;
                        return (
                          <div
                            key={item.id}
                            id={optionId}
                            className="gx-cs-item hover:bg-blue-600 hover:text-white"
                            onClick={() => handleCountrySelect(item)}
                            role="option"
                            aria-selected={isSelected}
                            style={{
                              backgroundColor: isSelected
                                ? "#e3f2fd"
                                : optionId === activeOptionId
                                  ? "#f0f0f0"
                                  : undefined,
                            }}
                          >
                            <img
                              src={`${COUNTRY_BASE_PATH}${item.flagIcon}`}
                              alt=""
                              className="gx-cs-item-flag"
                              onError={(e) => {
                                e.currentTarget.style.visibility = "hidden";
                              }}
                            />
                            <div className="gx-cs-item-content">
                              <span className="gx-cs-item-name">
                                {item.name + " "}
                              </span>
                              <span className="gx-cs-item-currency">
                                {item.currency.code} - {item.currency.name}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {!isValid && (
          <div
            id={errorId}
            className="gx_input_validationmessage gx-font-regular"
            role="alert"
            aria-live="assertive"
          >
            {validationMessage}
          </div>
        )}

        {isOverMax && (
          <div
            id={maxLengthErrorId}
            className="gx_input_validationmessage gx-font-regular"
            role="alert"
            aria-live="assertive"
          >
            {t("amount_cannot_exceed")} {maxAmount?.toLocaleString()}
          </div>
        )}
      </div>
    </>
  );
};

export default AmountTextBox;
